<template>
  <div class="k-table-skeleton">
    <div v-for="row in rows" :key="row" class="k-table-skeleton__row">
      <div
        v-for="(header, i) in headers"
        :key="i"
        class="k-table-skeleton__cell"
        :class="{ 'k-table-skeleton__cell--end': header.align === 'end' }"
        :style="cellStyle(header)"
      >
        <span
          class="k-table-skeleton__bone"
          :class="{ 'k-table-skeleton__bone--fixed': isFixed(header) }"
          :style="boneStyle(header, row, i)"
        ></span>
      </div>
    </div>
  </div>
</template>

<script>
// Column-aware skeleton for v-data-table / v-data-table-server loading slots.
// Drop-in: pass the same `headers` array the table uses; bars line up roughly
// under each column, fixed-width columns (e.g. the action column) render as a dot.
const BONE_WIDTHS = ["78%", "55%", "68%", "48%", "62%"];

export default {
  props: {
    headers: {
      type: Array,
      required: true,
    },
    rows: {
      type: Number,
      default: 10,
    },
  },
  methods: {
    // A column is "fixed" when it has an explicit pixel width (the action/icon
    // column) or no title — render it as a small dot instead of a text bar.
    isFixed(header) {
      return !!header.width || !header.title;
    },
    cellStyle(header) {
      if (header.width) {
        return { flex: `0 0 ${header.width}px`, maxWidth: `${header.width}px` };
      }
      return { flex: "1 1 0" };
    },
    boneStyle(header, row, i) {
      if (this.isFixed(header)) return {};
      // Deterministic width variation so rows don't look mechanically uniform.
      return { width: BONE_WIDTHS[(row + i) % BONE_WIDTHS.length] };
    },
  },
};
</script>

<style scoped>
.k-table-skeleton {
  /* Gentle entrance. The minimum-display floor (smoothLoading mixin) keeps the
     skeleton on screen long enough for this fade + the sweep below to actually
     be seen, so a fast load reads as a calm beat rather than a flash. */
  animation: k-skeleton-reveal 0.35s ease;
}

.k-table-skeleton__row {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 52px;
  padding: 0 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.k-table-skeleton__cell {
  display: flex;
  align-items: center;
  overflow: hidden;
}

.k-table-skeleton__cell--end {
  justify-content: flex-end;
}

.k-table-skeleton__bone {
  position: relative;
  display: inline-block;
  height: 14px;
  border-radius: 7px;
  /* Tinted with the app's primary token so it stays in sync with the theme. */
  background: color-mix(in srgb, var(--primary) 22%, transparent);
  overflow: hidden;
}

.k-table-skeleton__bone--fixed {
  width: 26px;
  height: 26px;
  border-radius: 50%;
}

.k-table-skeleton__bone::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    color-mix(in srgb, var(--primary) 42%, transparent),
    transparent
  );
  /* Calm, unhurried sweep so it reads as ambient loading, not a twitch. */
  animation: k-skeleton-shimmer 1.6s ease-in-out infinite;
}

@keyframes k-skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}

@keyframes k-skeleton-reveal {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Respect users who ask for less motion: reveal instantly, drop the shimmer. */
@media (prefers-reduced-motion: reduce) {
  .k-table-skeleton {
    animation-duration: 0.001s;
  }

  .k-table-skeleton__bone::after {
    animation: none;
  }
}
</style>
